import Meeting from '../model/Meeting'
import moment, {Moment} from 'moment'
import React, {ReactElement, ReactNode} from 'react'
import MeetingSimple from '../model/MeetingSimple'
import Time from '../../../dateHelpers/Time'
import {Optional} from '../../../app/TypeHelpers'
import {CommentUser} from '../model/MeetingComment'
import {RESIDENT_STATUS, ROLE_FOR_ADMINISTRATOR, ROLE_FOR_RESIDENT, ROLE_TYPE} from '../../../CodeMaster'
import {Room} from '../model/Room'
import {AssocDirectorHelper} from './AssocDirectorHelper'
import {formatDatetimeSimple, formatDatetimeSlashSeparator} from '../../../formatters/formatDate'
import {
    InactiveUserIconLarge,
    InactiveUserIconMedium,
    InactiveUserIconSmall,
    UserIconLarge,
    UserIconMedium,
    UserIconSmall,
} from '../AssocDirectorCommon'

export interface DateFixedMeeting extends Pick<Meeting, 'id' | 'name' | 'startTime' | 'endTime' | 'place' | 'url' |
    'contents' | 'memo' | 'meetingAttachmentFileList' | 'meetingAttachmentLinkList'> {
    date: string
}

export class MeetingHelper {
    static checkPastMeeting(meeting: Meeting, currentDatetime: Moment): boolean {
        if (meeting.date === null) {
            return false
        }
        let meetingEndDatetime: Moment
        if (meeting.endTime !== null) {
            meetingEndDatetime = moment(`${meeting.date} ${meeting.endTime}`)
        } else {
            meetingEndDatetime = moment(`${meeting.date}`).endOf('day')
        }

        return meetingEndDatetime.isSameOrBefore(currentDatetime)
    }

    static getUserIconNodeByResidentStatus(
        isWithinTerm: boolean,
        hasMailAddress: boolean,
        role: ROLE_FOR_ADMINISTRATOR | ROLE_FOR_RESIDENT,
        roomList: Room[],
        size: 'small' | 'medium' | 'large',
    ): ReactNode {
        let isValidUser = false
        if (role !== ROLE_FOR_ADMINISTRATOR.ASSOC &&
            role.value.slice(0, 1) === ROLE_TYPE.ADMIN.value) {
            isValidUser = true
        } else {
            for (const room of roomList) {
                if (room.status === RESIDENT_STATUS.REGISTERED ||
                    room.status === RESIDENT_STATUS.BEFORE_OCCUPANCY) {
                    isValidUser = true
                }
            }
        }
        const judgeActiveUserIcon = isValidUser && isWithinTerm && hasMailAddress
        if (size === 'small') {
            return judgeActiveUserIcon ? <UserIconSmall/> : <InactiveUserIconSmall/>
        } else if (size === 'medium') {
            return judgeActiveUserIcon ? <UserIconMedium/> : <InactiveUserIconMedium/>
        } else if (size === 'large') {
            return judgeActiveUserIcon ? <UserIconLarge/> : <InactiveUserIconLarge/>
        }
    }

    static getMeetingDateString(meeting: MeetingSimple): string {
        if (meeting.date === null) {
            return '日時未定'
        }
        const dateMoment = moment(meeting.date)
        let timeString: string
        if (meeting.startTime !== null && meeting.endTime !== null) {
            const displayStartTime = Time.fromString(meeting.startTime).toJSON()
            const displayEndTime = Time.fromString(meeting.endTime).toJSON()
            timeString = `${displayStartTime} 〜 ${displayEndTime}`
        } else {
            timeString = '時間未定'
        }
        return `${dateMoment.format('YYYY年M月D日(ddd)')} ${timeString}`
    }

    static getMeetingPlaceString(meeting: MeetingSimple): string {
        let meetingPlaceString: string
        const placeInfos = []
        if (meeting.place !== null) {
            placeInfos.push(meeting.place)
        }
        if (meeting.url !== null) {
            placeInfos.push('オンライン参加可能')
        }
        if (placeInfos.length > 0) {
            meetingPlaceString = placeInfos.join('、')
        } else {
            meetingPlaceString = '場所未定'
        }
        return meetingPlaceString
    }

    static getNextMeeting(meetings: MeetingSimple[], currentDateTime: Moment): Optional<MeetingSimple> {
        const sortedMeetings = [...meetings]
            .filter(meeting => {
                return meeting.date
            })
            .sort((a, b) => {
                const startDateTimeA = `${a.date} ${a.startTime ?? ''}`
                const startDateTimeB = `${b.date} ${b.startTime ?? ''}`
                return (startDateTimeA === startDateTimeB) ? 0 :
                    (startDateTimeA > startDateTimeB) ? 1 : -1
            })

        const openMeetings = sortedMeetings
            .filter(meeting => {
                let startDateTime: Moment
                if (meeting.startTime !== null) {
                    startDateTime = moment(`${meeting.date} ${meeting.startTime}`)
                } else {
                    startDateTime = moment(`${meeting.date}`)
                }
                let endDateTime: Moment
                if (meeting.endTime !== null) {
                    endDateTime = moment(`${meeting.date} ${meeting.endTime}`)
                } else {
                    endDateTime = moment(`${meeting.date}`).endOf('day')
                }
                return currentDateTime.isSameOrAfter(startDateTime) && currentDateTime.isBefore(endDateTime)
            })

        const futureMeetings = sortedMeetings
            .filter(meeting => {
                let startDateTime: Moment
                if (meeting.startTime !== null) {
                    startDateTime = moment(`${meeting.date} ${meeting.startTime}`)
                } else {
                    startDateTime = moment(`${meeting.date}`)
                }
                return currentDateTime.isBefore(startDateTime)
            })

        let nextMeeting: Optional<MeetingSimple>
        if (openMeetings.length !== 0) {
            nextMeeting = openMeetings[openMeetings.length - 1]
        } else if (futureMeetings.length !== 0) {
            nextMeeting = futureMeetings[0]
        }

        return nextMeeting
    }

    static getRoleNameForComment(role: ROLE_FOR_ADMINISTRATOR | ROLE_FOR_RESIDENT): string {
        let roleName = ''
        if (role instanceof ROLE_FOR_ADMINISTRATOR
            && role !== ROLE_FOR_ADMINISTRATOR.ASSOC) {
            roleName = ` (${role.label})`
        }
        return roleName
    }

    static createCommentUserName(commentUser: CommentUser): string {
        return `${
            commentUser.familyName && commentUser.firstName ? `${commentUser.familyName} ${commentUser.firstName}` : '（未登録）'
        }${
            MeetingHelper.getRoleNameForComment(commentUser.role)
        }`
    }

    static createCommentUserNameByResidentStatus(commentUser: CommentUser): string {
        return `${
            AssocDirectorHelper.getDisplayUserNameByResidentStatus(
                commentUser.familyName,
                commentUser.firstName,
                commentUser.roomList)
        }${
            MeetingHelper.getRoleNameForComment(commentUser.role)
        }`
    }

    static getDisplayRoomListByResidentStatus(roomList: Room[]): ReactElement {
        let unregistered = false
        let abolition = false
        let registered = false
        for (const room of roomList) {
            if (room.status === RESIDENT_STATUS.UNREGISTERED) {
                unregistered = true
            }
            if (room.status === RESIDENT_STATUS.ABOLITION) {
                abolition = true
            }
            if (room.status === RESIDENT_STATUS.REGISTERED ||
                room.status === RESIDENT_STATUS.BEFORE_OCCUPANCY) {
                registered = true
            }
        }
        if (unregistered) {
            return <>{
                roomList
                    .map(room => `${room.ridgeName ?
                        (room.ridgeName + ' ') : ''}${Number(
                        room.roomNumber)}号室`)
                    .join(', ')
            }</>
        } else if (abolition && !registered) {
            const sortedAbolitionRoomList: Room[] = roomList
                .filter((room) => room.abolitionDatetime !== null)
                .sort((a, b) => {
                    if (a.abolitionDatetime === null || b.abolitionDatetime === null) {
                        return 0
                    }
                    if (a.abolitionDatetime === b.abolitionDatetime) {
                        return 0
                    }
                    return a.abolitionDatetime < b.abolitionDatetime ? 1 : -1
                })
            return <>{
                <s>{sortedAbolitionRoomList[0].ridgeName ?
                    (sortedAbolitionRoomList[0].ridgeName + ' ') : ''}{Number(
                    sortedAbolitionRoomList[0].roomNumber)}号室</s>
            }</>
        } else if (registered) {
            const registerRoomList: Room[] = roomList
                .filter((room) => room.status !== RESIDENT_STATUS.ABOLITION)
            return <>{
                registerRoomList
                    .map(room => `${room.ridgeName ?
                        (room.ridgeName + ' ') : ''}${Number(
                        room.roomNumber)}号室`)
                    .join(', ')
            }</>
        }
        return <></>
    }

    static getRoomListSortKeyByResidentStatus(roomList: Room[]): string {
        let unregistered = false
        let abolition = false
        let registered = false
        for (const room of roomList) {
            if (room.status === RESIDENT_STATUS.UNREGISTERED) {
                unregistered = true
            }
            if (room.status === RESIDENT_STATUS.ABOLITION) {
                abolition = true
            }
            if (room.status === RESIDENT_STATUS.REGISTERED ||
                room.status === RESIDENT_STATUS.BEFORE_OCCUPANCY) {
                registered = true
            }
        }
        if (unregistered) {
            return `${roomList[0].ridgeCd}${roomList[0].roomNumber}`
        } else if (abolition && !registered) {
            const sortedAbolitionRoomList: Room[] = roomList
                .filter((room) => room.abolitionDatetime !== null)
                .sort((a, b) => {
                    if (a.abolitionDatetime === null || b.abolitionDatetime === null) {
                        return 0
                    }
                    if (a.abolitionDatetime === b.abolitionDatetime) {
                        return 0
                    }
                    return a.abolitionDatetime < b.abolitionDatetime ? 1 : -1
                })
            return `${sortedAbolitionRoomList[0].ridgeCd}${sortedAbolitionRoomList[0].roomNumber}`
        } else if (registered) {
            const registerRoomList: Room[] = roomList
                .filter((room) => room.status !== RESIDENT_STATUS.ABOLITION)
            return `${registerRoomList[0].ridgeCd}${registerRoomList[0].roomNumber}`
        }
        return ''
    }

    static getDisplayRoomListOrRoleName(roleId: ROLE_FOR_ADMINISTRATOR, roomList: Room[]): ReactElement {
        if (roleId !== ROLE_FOR_ADMINISTRATOR.ASSOC) {
            return <>{roleId.label}</>
        }
        let unregistered = false
        let abolition = false
        let registered = false
        for (const room of roomList) {
            if (room.status === RESIDENT_STATUS.UNREGISTERED) {
                unregistered = true
            }
            if (room.status === RESIDENT_STATUS.ABOLITION) {
                abolition = true
            }
            if (room.status === RESIDENT_STATUS.REGISTERED ||
                room.status === RESIDENT_STATUS.BEFORE_OCCUPANCY) {
                registered = true
            }
        }
        if (unregistered) {
            return <>{
                roomList
                    .map(room => `${room.ridgeName ?
                        (room.ridgeName + ' ') : ''}${Number(
                        room.roomNumber)}`)
                    .join(', ')
            }</>
        } else if (abolition && !registered) {
            const sortedAbolitionRoomList: Room[] = roomList
                .filter((room) => room.abolitionDatetime !== null)
                .sort((a, b) => {
                    if (a.abolitionDatetime === null || b.abolitionDatetime === null) {
                        return 0
                    }
                    if (a.abolitionDatetime === b.abolitionDatetime) {
                        return 0
                    }
                    return a.abolitionDatetime < b.abolitionDatetime ? 1 : -1
                })
            return <>{
                <s>{sortedAbolitionRoomList[0].ridgeName ?
                    (sortedAbolitionRoomList[0].ridgeName + ' ') : ''}{Number(
                    sortedAbolitionRoomList[0].roomNumber)}</s>
            }</>
        } else if (registered) {
            const registerRoomList: Room[] = roomList
                .filter((room) => room.status !== RESIDENT_STATUS.ABOLITION)
            return <>{
                registerRoomList
                    .map(room => `${room.ridgeName ?
                        (room.ridgeName + ' ') : ''}${Number(
                        room.roomNumber)}`)
                    .join(', ')
            }</>
        }
        return <></>
    }

    static formatDoNotDisplayCurrentYear(currentDatetime: string, postDatetime: string): string {
        const currentDate = new Date(currentDatetime)
        const postDate = new Date(postDatetime)
        if (currentDate.getFullYear() === postDate.getFullYear()) {
            return formatDatetimeSimple(postDatetime)
        } else {
            return formatDatetimeSlashSeparator(postDatetime)
        }
    }
}
